import { useEffect, useState, useRef } from "react";
import { Upload, FileText, Trash2, RefreshCw, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Reusable startlist manager — upload PDF / paste text / clear / re-OCR.
 * Works for both photographers (own events) and admins (any event).
 *
 * Props:
 *   - eventId: required
 *   - onChanged: optional callback after upload/clear (e.g. refresh parent state)
 */
export default function StartlistManager({ eventId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reOcr, setReOcr] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const fileInput = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/events/${eventId}/startlist`);
      setData(r.data);
    } catch {
      setData({ numbers: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (eventId) load(); }, [eventId]); // eslint-disable-line

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/events/${eventId}/startlist/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`✓ Hittade ${r.data.count} startnummer i ${r.data.source}`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uppladdning misslyckades");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const handlePaste = async () => {
    if (!pasteText.trim()) return;
    setUploading(true);
    try {
      const r = await api.post(`/events/${eventId}/startlist/text`, { text: pasteText });
      toast.success(`✓ Hittade ${r.data.count} startnummer`);
      setPasteText("");
      setShowPaste(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Misslyckades");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Ta bort startlistan? OCR kommer återgå till fri detektering.")) return;
    try {
      await api.delete(`/events/${eventId}/startlist`);
      toast.success("Startlistan rensad");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Kunde inte rensa");
    }
  };

  const handleReOcr = async () => {
    if (!window.confirm("Kör om OCR mot startlistan? Endast bilder utan manuella taggar uppdateras.")) return;
    setReOcr(true);
    try {
      const r = await api.post(`/events/${eventId}/startlist/re-ocr`);
      toast.success(`✓ ${r.data.queued} foton köades. ${r.data.skipped_manual} manuellt-taggade hoppades över.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Misslyckades");
    } finally {
      setReOcr(false);
    }
  };

  if (loading) return <div className="text-white/50 text-sm py-4">Laddar...</div>;

  const numbers = data?.numbers || [];
  const hasList = numbers.length > 0;

  return (
    <div className="card-surface p-5" data-testid="startlist-manager">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="label-overline">Startlista</p>
          <h3 className="font-display font-bold uppercase text-white text-lg">
            {hasList ? (
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                {numbers.length} startnummer registrerade
              </span>
            ) : "Ingen startlista"}
          </h3>
          {hasList && (
            <p className="text-white/50 text-xs mt-1">
              {data.source && <>Källa: <span className="text-white/70">{data.source}</span> · </>}
              AI tvingas tagga inom denna lista
            </p>
          )}
          {!hasList && (
            <p className="text-white/50 text-xs mt-1">
              Ladda upp startlista (PDF) så blir AI:n betydligt bättre på otydliga startnummer.
            </p>
          )}
        </div>
      </div>

      {hasList && (
        <div className="bg-black/30 border border-white/10 p-3 mb-3 max-h-32 overflow-y-auto" data-testid="startlist-numbers">
          <div className="flex flex-wrap gap-1">
            {numbers.slice(0, 200).map((n) => (
              <span key={n} className="bg-brand/20 text-brand text-xs font-bold px-1.5 py-0.5">
                #{n}
              </span>
            ))}
            {numbers.length > 200 && (
              <span className="text-white/40 text-xs px-1.5 py-0.5">
                +{numbers.length - 200} fler
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
          data-testid="startlist-file-input"
        />
        <button
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="btn-primary text-sm py-2 inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="startlist-upload-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {hasList ? "Ersätt PDF" : "Ladda upp PDF"}
        </button>
        <button
          onClick={() => setShowPaste((s) => !s)}
          className="btn-secondary text-sm py-2 inline-flex items-center gap-2"
          data-testid="startlist-paste-btn"
        >
          <FileText className="w-4 h-4" /> Klistra in text
        </button>
        {hasList && (
          <>
            <button
              onClick={handleReOcr}
              disabled={reOcr}
              className="btn-secondary text-sm py-2 inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="startlist-re-ocr-btn"
              title="Kör om AI-taggning på alla otaggade bilder i eventet"
            >
              {reOcr ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Kör om OCR
            </button>
            <button
              onClick={handleClear}
              className="btn-secondary text-sm py-2 inline-flex items-center gap-2 text-red-300 hover:text-red-200"
              data-testid="startlist-clear-btn"
            >
              <Trash2 className="w-4 h-4" /> Rensa
            </button>
          </>
        )}
      </div>

      {showPaste && (
        <div className="mt-3 border border-white/10 p-3 bg-black/30">
          <p className="text-xs text-white/60 mb-2">
            Klistra in en text med startnummer (separerade med komma, mellanslag eller radbrytningar). Allt som inte är 1-4 siffror ignoreras.
          </p>
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="1, 7, 12, 23, 142, 999..."
            rows={5}
            className="input-field font-mono text-sm w-full"
            data-testid="startlist-paste-textarea"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setShowPaste(false); setPasteText(""); }}
              className="btn-secondary text-sm py-2"
            >
              Avbryt
            </button>
            <button
              onClick={handlePaste}
              disabled={uploading || !pasteText.trim()}
              className="btn-primary text-sm py-2 disabled:opacity-50"
              data-testid="startlist-paste-submit"
            >
              Spara
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
